<template>
  <div id="app" class="h-full bg-gray-50 dark:bg-gray-900">
    <!-- Header -->
    <header class="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
      <div class="max-w-7xl mx-auto pl-2 pr-4 sm:pl-3 sm:pr-6 lg:pl-4 lg:pr-8">
        <div class="flex justify-between items-center h-16">
          <!-- Logo and Brand -->
          <div class="flex items-center">
            <router-link to="/" class="flex items-center space-x-2">
              <img src="@/assets/purdia_logo.svg" alt="Purdia Logo" class="h-8 w-auto" />
              <span class="text-xl font-bold text-gray-900 dark:text-gray-50">Docs</span>
            </router-link>
          </div>

          <!-- Search Bar -->
          <div class="flex-1 max-w-lg mx-8">
            <SearchBar />
          </div>

          <!-- Right Navigation -->
          <div class="flex items-center space-x-4">
            <ThemeToggle />

            <button
              @click="toggleSidebar"
              class="lg:hidden p-2 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 rounded-md transition-colors duration-200"
              aria-label="Toggle sidebar"
            >
              <Bars3Icon class="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content with Consistent Sidebar -->
    <div class="flex h-[calc(100vh-4rem)]">
      <!-- Sidebar -->
      <Sidebar
        :is-collapsed="sidebarCollapsed"
        @toggle="(value: boolean) => (sidebarCollapsed = value)"
      />

      <!-- Main Content Area -->
      <main class="flex-1 overflow-auto bg-white dark:bg-gray-900">
        <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <!-- Breadcrumbs -->
          <Breadcrumbs />

          <!-- Page Content -->
          <router-view v-slot="{ Component }">
            <transition name="fade" mode="out-in">
              <component :is="Component" />
            </transition>
          </router-view>
        </div>
      </main>
    </div>

    <!-- Mobile Sidebar Overlay -->
    <div v-if="showMobileSidebar" class="fixed inset-0 z-40 lg:hidden" @click="closeMobileSidebar">
      <div class="fixed inset-0 bg-gray-600 bg-opacity-75 dark:bg-opacity-90"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { Bars3Icon } from '@heroicons/vue/24/outline'
  import Sidebar from '@/components/layout/Sidebar.vue'
  import SearchBar from '@/components/search/SearchBar.vue'
  import Breadcrumbs from '@/components/navigation/Breadcrumbs.vue'
  import ThemeToggle from '@/components/ui/ThemeToggle.vue'
  import { useThemeStore } from '@/stores/theme'

  // Store
  const themeStore = useThemeStore()

  // Route
  const route = useRoute()

  // Reactive state
  const sidebarCollapsed = ref(false)
  const showMobileSidebar = ref(false)

  // Computed
  const isMobile = computed(() => window.innerWidth < 1024)

  const toggleSidebar = () => {
    if (isMobile.value) {
      showMobileSidebar.value = !showMobileSidebar.value
    } else {
      sidebarCollapsed.value = !sidebarCollapsed.value
    }
  }

  const closeMobileSidebar = () => {
    showMobileSidebar.value = false
  }

  // Watch for route changes to close mobile sidebar
  watch(route, () => {
    if (showMobileSidebar.value) {
      closeMobileSidebar()
    }
  })

  // Lifecycle
  onMounted(() => {
    try {
      // Initialize theme
      themeStore.initializeTheme()

      // Set initial sidebar state based on screen size
      sidebarCollapsed.value = window.innerWidth < 1280

      // Handle window resize
      const handleResize = () => {
        if (window.innerWidth >= 1024) {
          showMobileSidebar.value = false
        }
        if (window.innerWidth >= 1280) {
          sidebarCollapsed.value = false
        }
      }

      window.addEventListener('resize', handleResize)

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize)
      }
    } catch (error) {
      console.error('Error in App.vue onMounted:', error)
    }
  })
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  /* Theme transitions */
  :global(.light),
  :global(.dark) {
    transition:
      background-color 0.3s ease,
      color 0.3s ease,
      border-color 0.3s ease;
  }
</style>
